import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { makeRequest } from "../makeRequest";
import { MY_ENDPOINTS } from "@raashidh/shared";

/* ==========================================================
   Type Definition
========================================================== */
export interface Transaction {
  id: number;
  session_id: string;
  order_id: number;
  user_id?: number;
  package_id?: number;
  amount: number;
  currency: string;
  vendor: string;
  vendor_transaction_id?: string;
  vendor_payment_url?: string;
  vendor_payment_short_url?: string;
  qr?: string;
  state:
    | "INITIATED"
    | "PENDING"
    | "CONFIRMED"
    | "FAILED"
    | "CANCELLED"
    | "REFUND_REQUESTED"
    | "REFUNDED";
  created_at: string;
  updated_at: string;
}

/* ==========================================================
   API Endpoints
========================================================== */
const TRANSACTIONS_ENDPOINT = MY_ENDPOINTS.PAYMENT_ENDPOINTS.HAS_PAYMENT_LINK.path; 
const GET_TRANSACTION_ENDPOINT = MY_ENDPOINTS.PAYMENT_ENDPOINTS.HAS_PAYMENT_LINK.path;
const PAYNOW_ENDPOINT = "/payment/paynow";
const PAYSTATE_ENDPOINT = "/payment/getstate";
const CREATE_PAYMENT_URL_ENDPOINT = MY_ENDPOINTS.PAYMENT_ENDPOINTS.CREATE_PAYMENT_LINK.path;
const PAYPOINTS_ENDPOINT = "/payaskwithpoints";

export function useGetTransaction(entity_id?: number, entity_type: string = "order") {
  return useQuery({
    queryKey: ["transaction", entity_id, entity_type],
    queryFn: async () => {
      if (!entity_id) throw new Error("No entity_id provided");
      const res = await makeRequest(`${GET_TRANSACTION_ENDPOINT}?entity_type=${entity_type}&entity_id=${entity_id}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "Authorization": `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (!res.success || !res.data) {
        throw new Error("Failed to load transaction");
      }

      return res.data;
    },
    enabled: !!entity_id,            // Only run when entity_id exists
    refetchOnWindowFocus: false,  // Don’t re-run on tab focus
    refetchOnMount: false,        // Don’t re-run automatically
    refetchOnReconnect: false,    // Don’t re-run on network reconnect
  });
}


export interface CreatePayLinkVariables {
  order_id?: number | string;
  entity_type: string;
  entity_id: string | number;
  amount: number;
  currency: string;
  provider?: "bml" | "stripe" | "manual";
}

export function useCreatePayLink() {
  const queryClient = useQueryClient();

  return useMutation<PaymentLinkResponse, Error, CreatePayLinkVariables>({
    mutationFn: async (data) => {
      const res = await makeRequest(CREATE_PAYMENT_URL_ENDPOINT, {
        method: "POST",
        body: {
          ...data,
          provider: data.provider ?? "bml",
          redirect_base_url: window.location.origin + "/payment/callback",
        },
      });

      if (!res) throw new Error("No response from server");
      return res.data ?? res;
    },

    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["transactions", variables.entity_type, variables.entity_id],
      });
    },
  });
}

/* ==========================================================
   Fetch All Transactions
========================================================== */
export function useTransactions(userId?: number | string) {
  return useQuery<Transaction[], Error>({
    queryKey: ["transactions", userId],
    queryFn: async () => {
      const res = await makeRequest(TRANSACTIONS_ENDPOINT, {
        method: "GET",
        query: userId ? { user_id: userId } : undefined,
      });
      if (!res) throw new Error("No response from server");
      return res.data ?? res;
    },
  });
}

/* ==========================================================
   Create a Payment Session (PayNow)
========================================================== */
interface PaymentLinkResponse {
  payment_url: string;
  session_id?: string;
  [key: string]: any;
}

export function useCreatePaymentLink(amount?: number, entity_id?: string) {
  return useQuery<PaymentLinkResponse, Error>({
    queryKey: ["paynow", amount, entity_id],
    enabled: Boolean(amount && entity_id), // Only run if both exist
    queryFn: async () => {
      if (!amount || !entity_id) throw new Error("Missing amount or entity_id");

      const res = await makeRequest(PAYNOW_ENDPOINT, {
        method: "GET",
        query: { amount, entity_id },
      });

      if (!res) throw new Error("No response from server");
      return res.data ?? res;
    },
  });
}

/* ==========================================================
   GET Payment State
========================================================== */
export function usePayState(id?: any) {
  return useQuery({
    queryKey: ["payment_state", id],
    enabled: Boolean(id), // ✅ ensures it only runs when id is provided
    queryFn: async () => {
      const res = await makeRequest(PAYSTATE_ENDPOINT, {
        method: "GET",
        query: { id }, // ✅ simpler conditional
      });

      if (!res) throw new Error("No response from server");

      return res.data ?? res; // ✅ supports both response shapes
    },
  });
}

type PayWithPointsResponse = {
  success: boolean;
  message?: string;
  data?: any;
  userPoints?: number;
  requiredPoints?: number;
};

export function usePayWithPoints() {
  return useMutation<PayWithPointsResponse, Error, number>({
    mutationKey: ["paywithpoints"],
    mutationFn: async (entity_id: number) => {
      if (!entity_id) throw new Error("Missing entity_id");

      const res = await fetch(PAYPOINTS_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ entity_id }), // ✅ Must send JSON body
      });

      if (!res.ok) {
        let errMsg = "Payment failed";
        try {
          const errData = await res.json();
          errMsg = errData?.error || errMsg;
        } catch {}
        throw new Error(errMsg);
      }

      const data = await res.json();
      return data; // ✅ parse JSON response
    },
  });
}